/**
 * Rules editor modal (Spec v0.3).
 *
 * List + add + edit + delete vendor rules. Import/export JSON v2.
 */

import { el, toast, openModal } from './dom.js';
import {
  listRules,
  saveRule,
  removeRule,
  removeAllRules,
  exportRulesJson,
  importRulesJson,
  vendorKey
} from '../lib/rules.js';

const RULE_FIELD_HELP = {
  vendorMatch: {
    title: 'Vendor match',
    body: 'Text used to identify invoices from this vendor. Keep it specific enough to avoid matching other vendors with similar names.',
    example: 'CITY OF BAYTOWN UTILITY SERVICES'
  },
  glDefault: {
    title: 'Default GL',
    body: 'Fallback GL code for the rule. In detail mode, this GL is used whenever no line-item rule matches a line.',
    example: '6000'
  },
  propertyDefault: {
    title: 'Default property',
    body: 'Fallback property code. A line-item rule can override this, but null property values in line-item rules fall back here.',
    example: '302 Red Bud'
  },
  taxGl: {
    title: 'Tax GL',
    body: 'GL code used for emitted sales tax rows in detail mode. If left blank, the app uses the default GL value.',
    example: '6000'
  },
  emitDistributionLines: {
    title: 'Emit distribution lines',
    body: 'On: export one row per non-zero line item plus tax rows (detail mode). Off: export one summary row per invoice.',
    example: 'On for repair/trade vendors with mixed coding.\nOff for utilities or single-code vendors.'
  },
  lineItemJson: {
    title: 'Line-item keywords (easy mode)',
    body: 'You can type simple keywords (one per line, or comma-separated). Advanced users can still paste JSON.',
    example: 'truck\nsewer\nrecycle'
  },
  note: {
    title: 'Note',
    body: 'Optional internal note for why the rule exists or exceptions to remember. This does not affect matching or export behavior.',
    example: 'Use this for invoices after 2026 contract update.'
  }
};

let helpPopoverSeq = 0;

function createRuleFieldHelp(fieldId) {
  const help = RULE_FIELD_HELP[fieldId];
  if (!help) return null;

  const popId = `rule-help-${fieldId}-${++helpPopoverSeq}`;
  const btn = el('button', {
    type: 'button',
    class: 'rule-help-btn',
    title: `Help: ${help.title}`,
    'aria-label': `Help for ${help.title}`
  }, ['?']);
  btn.setAttribute('popovertarget', popId);
  btn.setAttribute('popovertargetaction', 'toggle');

  const popover = el('div', {
    id: popId,
    class: 'rule-help-popover',
    popover: 'auto'
  }, [
    el('div', { class: 'rule-help-popover-title' }, [help.title]),
    el('p', { class: 'rule-help-popover-body' }, [help.body]),
    el('pre', { class: 'rule-help-example input-mono' }, [help.example])
  ]);

  return el('span', { class: 'rule-help-wrap' }, [btn, popover]);
}

function fieldLabelWithHelp(text, fieldId) {
  return el('div', { class: 'field-label-row' }, [
    el('span', { class: 'field-label' }, [text]),
    createRuleFieldHelp(fieldId)
  ]);
}

/**
 * @param {() => void} [onChange]
 * @param {Partial<{ vendorMatch: string, glCodeDefault: string, propertyCodeDefault: string, taxGl: string, emitDistributionLines: boolean, lineItemRules: unknown[], note: string }>} [draft] Prefill the add form (e.g. “Save as rule” from review).
 * @param {{ createThenEditFromDraft?: boolean, openEditRuleId?: string }} [options]
 */
export function openRulesEditor(onChange, draft = null, options = {}) {
  let rules = [];
  let editingId = options.openEditRuleId || null;
  let pendingCreateDraft = options.createThenEditFromDraft && draft ? { ...draft } : null;
  let pendingEditId = null;

  const close = openModal((closeFn) => {
    const card = el('div');
    card.appendChild(el('div', { class: 'modal-head' }, [
      el('h2', {}, ['Vendor ', el('em', {}, ['rules'])]),
      el('div', { class: 'modal-sub' }, [
        'Match vendor names to GL / property. Summary = one CSV row per invoice. Detail = one row per line item + tax (v0.3).'
      ])
    ]));

    const body = el('div', { class: 'modal-body' });

    const vendorIn = el('input', { type: 'text', placeholder: 'Vendor match string' });
    const glIn     = el('input', { type: 'text', class: 'input-mono', placeholder: 'Default GL code' });
    const propIn   = el('input', { type: 'text', class: 'input-mono', placeholder: 'Default property code' });
    const taxGlIn  = el('input', { type: 'text', class: 'input-mono', placeholder: 'Tax GL (detail mode)' });
    const emitChk  = el('input', { type: 'checkbox', id: 'emit-dist' });
    const lineJson = el('textarea', {
      rows: 4,
      class: 'input-mono',
      style: { width: '100%', fontSize: '12px' },
      placeholder: 'Easy mode: truck\\nsewer\\nrecycle\\n\\n(Advanced: JSON array)'
    });
    const noteIn   = el('input', { type: 'text', placeholder: 'Note (optional)' });

    if (draft) {
      vendorIn.value = String(draft.vendorMatch ?? '').trim();
      glIn.value = String(draft.glCodeDefault ?? '').trim();
      propIn.value = String(draft.propertyCodeDefault ?? '').trim();
      taxGlIn.value = String(draft.taxGl ?? draft.glCodeDefault ?? '').trim();
      emitChk.checked = !!draft.emitDistributionLines;
      if (Array.isArray(draft.lineItemRules) && draft.lineItemRules.length) {
        lineJson.value = JSON.stringify(draft.lineItemRules, null, 2);
      }
      if (draft.note != null) noteIn.value = String(draft.note);
    }

    const onAdd = async () => {
      let lineItemRules = [];
      if (emitChk.checked && lineJson.value.trim()) {
        try {
          lineItemRules = parseLineItemRulesInput(
            lineJson.value,
            glIn.value.trim(),
            propIn.value.trim()
          );
        } catch (e) {
          toast(`Line-item rules: ${e.message}`, 'error');
          return;
        }
      }
      try {
        await saveRule({
          vendorMatch: vendorIn.value,
          glCodeDefault: glIn.value,
          propertyCodeDefault: propIn.value,
          taxGl: taxGlIn.value || glIn.value,
          emitDistributionLines: emitChk.checked,
          lineItemRules,
          note: noteIn.value
        });
        vendorIn.value = '';
        glIn.value = '';
        propIn.value = '';
        taxGlIn.value = '';
        emitChk.checked = false;
        lineJson.value = '';
        noteIn.value = '';
        vendorIn.focus();
        await refresh();
        toast('Rule added.', 'success');
      } catch (err) { toast(err.message, 'error'); }
    };

    body.appendChild(el('div', { class: 'rule-add-row' }, [
      el('div', {}, [fieldLabelWithHelp('Vendor match', 'vendorMatch'), vendorIn]),
      el('div', {}, [fieldLabelWithHelp('Default GL', 'glDefault'), glIn]),
      el('div', {}, [fieldLabelWithHelp('Default property', 'propertyDefault'), propIn]),
      el('div', {}, [fieldLabelWithHelp('Tax GL', 'taxGl'), taxGlIn]),
      el('div', { class: 'rule-checkbox-row' }, [
        el('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } }, [
          emitChk,
          'Emit distribution lines (detail mode)'
        ]),
        createRuleFieldHelp('emitDistributionLines')
      ]),
      el('div', {}, [fieldLabelWithHelp('Line-item keywords (easy) / JSON (advanced)', 'lineItemJson'), lineJson]),
      el('div', {}, [fieldLabelWithHelp('Note', 'note'), noteIn]),
      el('div', { style: { display: 'flex', alignItems: 'flex-end' } }, [
        el('button', { class: 'primary-btn', onClick: onAdd }, ['Add'])
      ])
    ]));

    const tableHost = el('div', { class: 'rules-table-wrap' });
    body.appendChild(tableHost);

    const actionRow = el('div', {
      style: { display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }
    }, [
      el('button', {
        class: 'ghost-btn',
        onClick: async () => {
          try {
            const json = await exportRulesJson();
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = el('a', { href: url, download: `wiseentry-rules-${stamp()}.json` });
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast('Rules exported.', 'success');
          } catch (err) { toast(err.message, 'error'); }
        }
      }, ['Export rules…']),
      el('button', {
        class: 'ghost-btn',
        onClick: () => importRulesFlow(refresh)
      }, ['Import rules…']),
      el('div', { style: { flex: '1' } }),
      el('button', {
        class: 'danger-btn',
        onClick: async () => {
          if (rules.length === 0) return;
          if (!confirm(`Delete all ${rules.length} rule${rules.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
          await removeAllRules();
          toast('All rules deleted.', 'info');
          await refresh();
        }
      }, ['Delete all rules'])
    ]);
    body.appendChild(actionRow);

    card.appendChild(body);
    card.appendChild(el('div', { class: 'modal-foot' }, [
      el('button', { class: 'primary-btn', onClick: closeFn }, ['Done'])
    ]));

    const refresh = async () => {
      if (pendingCreateDraft) {
        const created = await saveRule(pendingCreateDraft);
        pendingEditId = created.id;
        pendingCreateDraft = null;
        toast('Rule created. Review and save changes.', 'success');
      }
      rules = await listRules();
      if (pendingEditId && rules.some(r => r.id === pendingEditId)) {
        editingId = pendingEditId;
        pendingEditId = null;
      }
      drawTable(tableHost, rules, editingId, {
        onDelete: async (r) => {
          if (!confirm(`Delete rule for "${vendorKey(r)}"?`)) return;
          await removeRule(r.id);
          if (editingId === r.id) editingId = null;
          toast('Rule deleted.', 'info');
          await refresh();
        },
        onEdit: (r) => {
          editingId = editingId === r.id ? null : r.id;
          refresh();
        },
        onSaveEdit: async (r, patch) => {
          try {
            await saveRule({ ...r, ...patch, id: r.id });
            editingId = null;
            await refresh();
            toast('Rule saved.', 'success');
          } catch (err) { toast(err.message, 'error'); }
        }
      });
      if (onChange) onChange();
    };
    refresh();

    return card;
  }, { large: true, onClose: () => { if (onChange) onChange(); } });

  return close;
}

function drawTable(host, rules, editingId, { onDelete, onEdit, onSaveEdit }) {
  while (host.firstChild) host.removeChild(host.firstChild);

  if (rules.length === 0) {
    host.appendChild(el('div', { class: 'rule-empty' }, [
      'No rules yet. Add one above, or import a saved set.'
    ]));
    return;
  }

  const table = el('table', { class: 'rules' });
  table.appendChild(el('thead', {}, [
    el('tr', {}, [
      el('th', {}, ['Vendor']),
      el('th', {}, ['Mode']),
      el('th', {}, ['GL']),
      el('th', {}, ['Property']),
      el('th', {}, ['Tax GL']),
      el('th', {}, ['Match values']),
      el('th', { style: { width: '120px' } }, [''])
    ])
  ]));

  const tbody = el('tbody');
  for (const r of rules) {
    const isEditing = editingId === r.id;

    let glI = null;
    let prI = null;
    let taxI = null;
    let emit = null;
    let ej = null;

    /** @type {HTMLElement[]} */
    const actionBtns = [];

    if (isEditing) {
      ej = el('textarea', {
        rows: 4,
        class: 'input-mono',
        style: { width: '100%' },
        value: formatLineItemRulesForInput(r.lineItemRules || [])
      });
      taxI = el('input', { type: 'text', class: 'input-mono', value: r.taxGl || '' });
      emit = el('input', { type: 'checkbox', checked: !!r.emitDistributionLines });
      glI = el('input', { type: 'text', class: 'input-mono', value: r.glCodeDefault || '' });
      prI = el('input', { type: 'text', class: 'input-mono', value: r.propertyCodeDefault || '' });

      const runSaveEdit = () => {
        let lineItemRules = [];
        if (emit.checked && ej.value.trim()) {
          try {
            lineItemRules = parseLineItemRulesInput(
              ej.value,
              glI.value.trim(),
              prI.value.trim()
            );
          } catch (err) {
            toast(err.message, 'error');
            return;
          }
        }
        onSaveEdit(r, {
          glCodeDefault: glI.value.trim(),
          propertyCodeDefault: prI.value.trim(),
          taxGl: taxI.value.trim() || glI.value.trim(),
          emitDistributionLines: emit.checked,
          lineItemRules
        });
      };

      actionBtns.push(
        el('button', {
          class: 'primary-btn',
          style: { fontSize: '12px', padding: '6px 10px' },
          type: 'button',
          title: 'Save edits to this rule',
          onClick: runSaveEdit
        }, ['Save'])
      );
    }

    actionBtns.push(
      el('button', { class: 'ghost-btn', style: { fontSize: '12px' }, onClick: () => onEdit(r) }, [
        isEditing ? 'Close' : 'Edit'
      ]),
      el('button', {
        class: 'icon-btn',
        title: 'Delete',
        onClick: () => onDelete(r)
      }, ['×'])
    );

    tbody.appendChild(el('tr', {}, [
      el('td', { title: vendorKey(r) }, [vendorKey(r)]),
      el('td', {}, [r.emitDistributionLines ? 'Detail' : 'Summary']),
      el('td', {}, [r.glCodeDefault || '—']),
      el('td', {}, [r.propertyCodeDefault || '—']),
      el('td', {}, [r.taxGl || '—']),
      el('td', {}, [ruleMatchPreview(r)]),
      el('td', {}, [
        el('div', { class: 'rule-actions' + (isEditing ? ' rule-actions-edit' : '') }, actionBtns)
      ])
    ]));

    if (isEditing) {
      tbody.appendChild(el('tr', {}, [
        el('td', { colspan: 7 }, [
          el('div', {
            style: { padding: '10px 0', display: 'grid', gap: '10px' }
          }, [
            el('div', {}, [fieldLabelWithHelp('Default GL', 'glDefault'), glI]),
            el('div', {}, [fieldLabelWithHelp('Default property', 'propertyDefault'), prI]),
            el('div', {}, [fieldLabelWithHelp('Tax GL', 'taxGl'), taxI]),
            el('div', { class: 'rule-checkbox-row' }, [
              el('label', { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, [emit, 'Emit distribution lines']),
              createRuleFieldHelp('emitDistributionLines')
            ]),
            el('div', {}, [fieldLabelWithHelp('Line-item keywords (easy) / JSON (advanced)', 'lineItemJson'), ej])
          ])
        ])
      ]));
    }
  }
  table.appendChild(tbody);
  host.appendChild(table);
}

function ruleMatchPreview(rule) {
  const lineRules = Array.isArray(rule?.lineItemRules) ? rule.lineItemRules : [];
  if (!lineRules.length) {
    return el('span', { class: 'rule-match-empty' }, ['default only']);
  }

  const wrap = el('div', { class: 'rule-match-preview' });
  for (const lr of lineRules.slice(0, 3)) {
    const kind = lr?.matchOn === 'itemCode' ? 'itemCode' : 'keyword';
    const value = String(lr?.matchValue || '').trim() || '—';
    wrap.appendChild(el('span', { class: 'rule-match-chip', title: `${kind}: ${value}` }, [
      `${kind}: ${value}`
    ]));
  }
  if (lineRules.length > 3) {
    wrap.appendChild(el('span', { class: 'rule-match-more' }, [`+${lineRules.length - 3}`]));
  }
  return wrap;
}

function importRulesFlow(refresh) {
  const input = el('input', { type: 'file', accept: 'application/json' });
  input.addEventListener('change', async () => {
    const file = input.files && input.files[0];
    if (!file) return;
    const text = await file.text();
    const mode = confirm(
      'Replace all current rules with the imported set?\n\n' +
      '“OK” = replace (current rules deleted)\n' +
      '“Cancel” = merge (add to current rules)'
    ) ? 'replace' : 'merge';
    try {
      const { added } = await importRulesJson(text, mode);
      toast(`Imported ${added} rule${added === 1 ? '' : 's'}.`, 'success');
      if (refresh) await refresh();
    } catch (err) {
      toast(err.message, 'error', 5000);
    }
  });
  input.click();
}

function stamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

function parseLineItemRulesInput(raw, defaultGl, defaultProperty) {
  const text = String(raw || '').trim();
  if (!text) return [];

  // Advanced mode: accept JSON array (or single object).
  if (text.startsWith('[') || text.startsWith('{')) {
    const parsed = JSON.parse(text);
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    if (!Array.isArray(arr)) throw new Error('Must be a JSON array or object.');
    return arr;
  }

  // Easy mode: one keyword per line or comma-separated.
  const keywords = text
    .split(/\r?\n|,/g)
    .map(s => s.trim())
    .filter(Boolean);
  if (!keywords.length) return [];

  return keywords.map(k => ({
    matchOn: 'keyword',
    matchValue: k,
    glCode: defaultGl || '',
    propertyCode: defaultProperty || null
  }));
}

function formatLineItemRulesForInput(lineItemRules) {
  const arr = Array.isArray(lineItemRules) ? lineItemRules : [];
  if (!arr.length) return '';
  const allKeyword = arr.every(r => r && r.matchOn === 'keyword' && r.matchValue != null);
  if (allKeyword) {
    return arr.map(r => String(r.matchValue)).join('\n');
  }
  return JSON.stringify(arr, null, 2);
}
